import { isValidWorkboardBoardId } from "@openclaw/workboard-contract";
import type { RouteId } from "./app-route-paths.ts";
import type {
  NativeDeviceSettingsCapability,
  NativeDeviceSettingsSnapshot,
} from "./app/native-device-settings.ts";
import type { IconName } from "./components/icon-data.ts";
import { t } from "./i18n/index.ts";

export type NavigationRouteId = RouteId;

type NavigationPresentation = readonly [icon: IconName, titleKey: string, subtitleKey: string];

// Pages derives its built-in catalog from these destinations. Personal rail
// pins reference the catalog without changing its availability. Chat has the
// Sessions view; Settings/Docs remain in the profile menu.
// Skills and Skill Workshop are reached from the Plugins workspace, not sidebar items.
// Worktrees is a tab of the Sessions hub, so it is not listed either.
// Workboard is plugin-owned and enters the zone through its Control UI descriptor.
export const SIDEBAR_NAV_ROUTES = [
  "agents-home",
  "dashboards",
  "usage",
  "cron",
  "sessions",
  "systems",
  "activity",
  "meetings",
  "plugins",
  "apps",
  "portals",
] as const satisfies readonly NavigationRouteId[];

// Routes presented as tabs of the Plugins hub. The sidebar highlights the
// Plugins entry for all of them, mirroring how config covers settings routes.
export function isPluginsHubRoute(routeId: NavigationRouteId): boolean {
  return routeId === "plugins" || routeId === "skills" || routeId === "skill-workshop";
}

// Worktrees renders as a tab of the Sessions hub; the sidebar highlights the
// Sessions entry for both routes, mirroring the Plugins hub behavior.
export function isSessionsHubRoute(routeId: NavigationRouteId): boolean {
  return routeId === "sessions" || routeId === "worktrees";
}

export type SidebarNavRoute = (typeof SIDEBAR_NAV_ROUTES)[number];
export type PersistedSidebarRoute = SidebarNavRoute;

function isPersistedSidebarRoute(value: unknown): value is PersistedSidebarRoute {
  return SIDEBAR_NAV_ROUTES.includes(value as PersistedSidebarRoute);
}

export type SidebarZoneEntry =
  | { type: "route"; route: PersistedSidebarRoute }
  | { type: "plugin"; key: string }
  | { type: "session"; key: string }
  | { type: "person"; profileId: string };

// The rail starts clean; only user-added shortcuts occupy the pins region.
export const DEFAULT_SIDEBAR_ENTRIES: string[] = [];

/**
 * Parse the compact persisted representation used by browser and synced prefs.
 */
export function parseSidebarEntry(value: unknown): SidebarZoneEntry | null {
  if (typeof value !== "string") {
    return null;
  }
  if (value.startsWith("route:")) {
    const route = value.slice("route:".length);
    if (route === "workboard") {
      return { type: "plugin", key: "workboard/workboard" };
    }
    return isPersistedSidebarRoute(route) ? { type: "route", route } : null;
  }
  if (value.startsWith("person:")) {
    const profileId = value.slice("person:".length).trim();
    if (!profileId || /\s/u.test(profileId)) {
      return null;
    }
    for (let index = 0; index < profileId.length; index += 1) {
      if (profileId.charCodeAt(index) < 32) {
        return null;
      }
    }
    return { type: "person", profileId };
  }
  if (value.startsWith("session:")) {
    const key = value.slice("session:".length).trim();
    return key ? { type: "session", key } : null;
  }
  if (value.startsWith("workboard:")) {
    const boardId = value.slice("workboard:".length).trim();
    // Normalize the shipped Workboard pin format at the preference boundary.
    return isValidWorkboardBoardId(boardId)
      ? { type: "plugin", key: `workboard/board-${boardId}` }
      : null;
  }
  if (value.startsWith("plugin:")) {
    const key = value.slice("plugin:".length);
    // Descriptor ids are opaque, unlike native registration ids. The catalog
    // controls availability; preserving a key never grants access to a plugin.
    const separator = key.indexOf("/");
    return separator > 0 && separator < key.length - 1 ? { type: "plugin", key } : null;
  }
  return null;
}

export function serializeSidebarEntry(entry: SidebarZoneEntry): string {
  if (entry.type === "route") {
    return `route:${entry.route}`;
  }
  if (entry.type === "person") {
    return `person:${entry.profileId}`;
  }
  return entry.type === "plugin" ? `plugin:${entry.key}` : `session:${entry.key}`;
}

/**
 * Normalize a persisted sidebar-zone list. Returns null when the value is not a
 * list; malformed and duplicate entries are dropped.
 */
export function normalizeSidebarEntries(value: unknown): string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const normalized: string[] = [];
  for (const valueEntry of value) {
    const parsed = parseSidebarEntry(valueEntry);
    if (!parsed) {
      continue;
    }
    const entry = serializeSidebarEntry(parsed);
    if (!normalized.includes(entry)) {
      normalized.push(entry);
    }
  }
  return normalized;
}

type SettingsNavigationGroup = {
  /** i18n key for the group heading; null renders the group without a label. */
  labelKey: string | null;
  routes: readonly NavigationRouteId[];
};

export type SettingsSearchBlock = {
  routeId: RouteId;
  label: string;
  pathname?: string;
  search?: string;
  hash: string;
};

// Grouping feeds the full-page settings sidebar (settings-sidebar.ts). Ordered
// by user attention: personal/look-and-feel first, system plumbing last.
// Management surfaces (sessions, worktrees, activity, memory import) are
// workspace destinations, not settings; model setup is a subpage of Models.
const SETTINGS_NAVIGATION_GROUPS = [
  { labelKey: null, routes: ["custodian", "profile", "appearance", "notifications"] },
  { labelKey: "nav.settingsGroupDevice", routes: ["device", "device-permissions"] },
  {
    labelKey: "nav.settingsGroupConnections",
    routes: ["connection", "channels", "communications", "talk", "devices", "cloud-workers"],
  },
  {
    labelKey: "nav.settingsGroupAgents",
    routes: [
      "agents",
      "model-providers",
      "search",
      "plugin-settings",
      "skill-settings",
      "mcp",
      "memory",
      "automation",
    ],
  },
  {
    labelKey: "nav.settingsGroupSecurity",
    routes: ["security", "secrets", "approvals"],
  },
  {
    labelKey: "nav.settingsGroupSystem",
    routes: ["infrastructure", "labs", "advanced", "debug", "logs", "updates", "about"],
  },
] as const satisfies readonly SettingsNavigationGroup[];

const NON_ADMIN_SETTINGS_ROUTES: ReadonlySet<NavigationRouteId> = new Set([
  "profile",
  "appearance",
  "notifications",
  "connection",
  "channels",
  "talk",
  "devices",
  "agents",
  "model-providers",
  "search",
  "plugin-settings",
  "skill-settings",
  "memory",
  "approvals",
  "advanced",
  "debug",
  "logs",
  "about",
]);

export function isSettingsNavigationRouteVisible(
  routeId: NavigationRouteId,
  canAdmin: boolean,
  nativeDeviceSettings: NativeDeviceSettingsCapability | null = null,
): boolean {
  if (routeId === "device") {
    return nativeDeviceSettings !== null;
  }
  if (routeId === "device-permissions") {
    const snapshot = nativeDeviceSettings?.snapshot;
    return Boolean(
      snapshot &&
      (snapshot.permissions.entries.length > 0 ||
        snapshot.permissions.location ||
        snapshot.capabilities?.activeComputerPresenceEnabled !== undefined),
    );
  }
  if (routeId === "updates") {
    return canAdmin || nativeDeviceSettings !== null;
  }
  return canAdmin || NON_ADMIN_SETTINGS_ROUTES.has(routeId);
}

export function deviceSettingsGroupLabelKey(
  snapshot?: NativeDeviceSettingsSnapshot | null,
): string {
  const device = snapshot?.device;
  if (device?.platform === "macos") {
    return "nav.settingsGroupDevice";
  }
  if (device?.platform === "linux" || device?.platform === "windows") {
    return "nav.settingsGroupThisComputer";
  }
  if (device?.platform === "ios") {
    if (device.formFactor === "phone") {
      return "nav.settingsGroupThisIPhone";
    }
    if (device.formFactor === "pad") {
      return "nav.settingsGroupThisIPad";
    }
  }
  return "nav.settingsGroupThisDevice";
}

export function visibleSettingsNavigationGroups(
  canAdmin: boolean,
  nativeDeviceSettings: NativeDeviceSettingsCapability | null = null,
): readonly SettingsNavigationGroup[] {
  return SETTINGS_NAVIGATION_GROUPS.map((group) => ({
    labelKey:
      group.labelKey === "nav.settingsGroupDevice"
        ? deviceSettingsGroupLabelKey(nativeDeviceSettings?.snapshot)
        : group.labelKey,
    routes: group.routes.filter((route) =>
      isSettingsNavigationRouteVisible(route, canAdmin, nativeDeviceSettings),
    ),
  })).filter((group) => group.routes.length > 0);
}

// Settings subpages render with settings chrome but stay out of the sidebar.
// Subpages with a visible owner keep that owner selected so users retain
// location context while completing the nested flow.
const SETTINGS_SUBPAGE_ROUTES: readonly NavigationRouteId[] = [
  // Legacy General redirects remain Settings-owned during router transitions.
  "config",
  "ai-agents",
  "model-setup",
  "lobsterdex",
];
export const SETTINGS_SEARCHABLE_SUBPAGE_ROUTES: readonly NavigationRouteId[] = ["ai-agents"];

const SETTINGS_NAVIGATION_ROUTES: ReadonlySet<NavigationRouteId> = new Set([
  ...SETTINGS_NAVIGATION_GROUPS.flatMap((group) => group.routes),
  ...SETTINGS_SUBPAGE_ROUTES,
]);

function navigationPresentation(icon: IconName, key: string): NavigationPresentation {
  return [icon, `tabs.${key}`, `subtitles.${key}`];
}

const NAVIGATION_PRESENTATION: Record<NavigationRouteId, NavigationPresentation> = {
  settings: ["settings", "nav.settings", "common.settingsSections"],
  "agents-home": navigationPresentation("bot", "agentsHome"),
  claws: navigationPresentation("bot", "claws"),
  agents: navigationPresentation("bot", "agents"),
  activity: navigationPresentation("activity", "activity"),
  meetings: navigationPresentation("book", "meetings"),
  apps: navigationPresentation("layoutGrid", "apps"),
  portals: navigationPresentation("monitor", "portals"),
  approvals: navigationPresentation("badgeCheck", "approvals"),
  workboard: navigationPresentation("kanban", "workboard"),
  worktrees: navigationPresentation("folder", "worktrees"),
  channels: navigationPresentation("link", "channels"),
  connection: navigationPresentation("radio", "connection"),
  sessions: navigationPresentation("fileText", "sessions"),
  systems: navigationPresentation("monitor", "systems"),
  usage: navigationPresentation("coins", "usage"),
  cron: navigationPresentation("calendarClock", "cron"),
  skills: navigationPresentation("bookOpenText", "skills"),
  "skill-settings": navigationPresentation("bookOpenText", "skills"),
  plugins: navigationPresentation("plug", "plugins"),
  "plugin-settings": navigationPresentation("plug", "plugins"),
  "skill-workshop": navigationPresentation("wrench", "skillWorkshop"),
  device: navigationPresentation("monitor", "device"),
  "device-permissions": navigationPresentation("shieldCheck", "devicePermissions"),
  devices: navigationPresentation("monitorSmartphone", "devices"),
  "cloud-workers": navigationPresentation("server", "cloudWorkers"),
  chat: navigationPresentation("messageSquare", "chat"),
  terminal: ["terminal", "terminal.title", "terminal.open"],
  dashboard: navigationPresentation("layoutDashboard", "chat"),
  dashboards: navigationPresentation("layoutDashboard", "dashboards"),
  custodian: navigationPresentation("lobster", "custodian"),
  config: ["settings", "nav.settings", "subtitles.config"],
  profile: navigationPresentation("circleUser", "profile"),
  communications: navigationPresentation("send", "communications"),
  appearance: navigationPresentation("palette", "appearance"),
  lobsterdex: navigationPresentation("bug", "lobsterdex"),
  automation: navigationPresentation("terminal", "automation"),
  mcp: navigationPresentation("wrench", "mcp"),
  memory: navigationPresentation("book", "memory"),
  search: navigationPresentation("search", "search"),
  talk: navigationPresentation("mic", "talk"),
  infrastructure: navigationPresentation("globe", "infrastructure"),
  labs: navigationPresentation("flaskConical", "labs"),
  updates: navigationPresentation("download", "updates"),
  about: navigationPresentation("fileText", "about"),
  "ai-agents": navigationPresentation("brain", "aiAgents"),
  "model-setup": navigationPresentation("spark", "modelSetup"),
  "model-providers": ["box", "routeTitles.modelProviders", "subtitles.modelProviders"],
  "memory-import": navigationPresentation("download", "memoryImport"),
  notifications: ["bell", "routeTitles.notifications", "subtitles.notifications"],
  security: navigationPresentation("shieldCheck", "security"),
  secrets: ["key", "tabs.secrets", "secretsStore.hint"],
  advanced: ["fileCode", "routeTitles.advanced", "subtitles.advanced"],
  debug: navigationPresentation("bug", "debug"),
  logs: navigationPresentation("scrollText", "logs"),
  plugin: navigationPresentation("plug", "plugin"),
  "new-session": ["plus", "newSession.title", "newSession.hint"],
};

export function isSettingsNavigationRoute(routeId: NavigationRouteId): boolean {
  return SETTINGS_NAVIGATION_ROUTES.has(routeId);
}

export function isSettingsTakeover(routeId: RouteId | undefined): boolean {
  return routeId !== undefined && isSettingsNavigationRoute(routeId);
}

export function navigationIconForRoute(routeId: NavigationRouteId): IconName {
  return NAVIGATION_PRESENTATION[routeId]?.[0] ?? "folder";
}

export function titleForRoute(routeId: NavigationRouteId, translate = t): string {
  const [, titleKey] = NAVIGATION_PRESENTATION[routeId];
  return translate(titleKey);
}

/** Window/tab title, attention leftmost because tabs truncate from the right.
 * Titles already ending in the brand skip the suffix to avoid duplication. */
export function formatDocumentTitle(options: {
  context: string;
  attentionCount?: number;
  brandName?: string;
}): string {
  const brandName = options.brandName ?? "OpenClaw";
  const base = options.context.endsWith(brandName)
    ? options.context
    : `${options.context} — ${brandName}`;
  if (options.attentionCount && options.attentionCount > 0) {
    return `(${options.attentionCount}) ${base}`;
  }
  return base;
}

export function subtitleForRoute(routeId: NavigationRouteId, translate = t): string {
  const subtitleKey = NAVIGATION_PRESENTATION[routeId][2];
  return translate(subtitleKey);
}
